import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getReviewsByFragrance,
  getReviewById,
  createReview,
  updateReview,
  deleteReview,
  toggleReviewLike,
  getUserLikedReviews,
  getUserLikesForReviews,
  getReviewLikesCounts,
  getUserReviewForFragrance,
  getFragranceAverageRating,
  getCurrentUserReviews,
  createReviewReply,
  getRepliesByReview,
  deleteReviewReply,
  reportReview,
  reportReply,
} from '../api/reviews'
import { useAuthStore } from '../data/useAuthStore'

// Re-export types for convenience
export type {
  IReview,
  IReviewWithUser,
  IReviewWithFragrance,
  IReviewReply,
  IReviewReplyWithUser,
} from '../api/reviews'

// ============================================================================
// Query Keys
// ============================================================================

export const reviewKeys = {
  all: ['reviews'] as const,
  lists: () => [...reviewKeys.all, 'list'] as const,
  list: (fragranceId: string) => [...reviewKeys.lists(), fragranceId] as const,
  details: () => [...reviewKeys.all, 'detail'] as const,
  detail: (id: string) => [...reviewKeys.details(), id] as const,
  averageRating: (fragranceId: string) => [...reviewKeys.all, 'averageRating', fragranceId] as const,
  userLikes: () => [...reviewKeys.all, 'userLikes'] as const,
  likesCounts: (reviewIds: string[]) => [...reviewKeys.all, 'likesCounts', reviewIds] as const,
  userReview: (fragranceId: string) => [...reviewKeys.all, 'userReview', fragranceId] as const,
  currentUserReviews: () => [...reviewKeys.all, 'currentUser'] as const,
  replies: (reviewId: string) => [...reviewKeys.all, 'replies', reviewId] as const,
}

// ============================================================================
// Reviews Queries
// ============================================================================

/**
 * Fetch reviews for a fragrance
 */
export function useReviewsByFragrance(fragranceId: string, page = 1, perPage = 10) {
  return useQuery({
    queryKey: reviewKeys.list(fragranceId),
    queryFn: () => getReviewsByFragrance(fragranceId, page, perPage),
    enabled: !!fragranceId,
  })
}

/**
 * Fetch a single review by ID
 */
export function useReview(reviewId: string) {
  return useQuery({
    queryKey: reviewKeys.detail(reviewId),
    queryFn: () => getReviewById(reviewId),
    enabled: !!reviewId,
  })
}

/**
 * Fetch average rating for a fragrance
 */
export function useFragranceAverageRating(fragranceId: string) {
  return useQuery({
    queryKey: reviewKeys.averageRating(fragranceId),
    queryFn: () => getFragranceAverageRating(fragranceId),
    enabled: !!fragranceId,
  })
}

/**
 * Fetch all liked review IDs for the current user
 */
export function useUserLikedReviews() {
  const userProfile = useAuthStore((state) => state.userProfile)
  
  return useQuery({
    queryKey: reviewKeys.userLikes(),
    queryFn: () => {
      if (!userProfile) {
        throw new Error('User profile not loaded')
      }
      return getUserLikedReviews(userProfile.id)
    },
    enabled: !!userProfile,
  })
}

/**
 * Fetch like status for specific reviews
 */
export function useUserLikesForReviews(reviewIds: string[]) {
  const userProfile = useAuthStore((state) => state.userProfile)
  
  return useQuery({
    queryKey: ['reviews', 'userLikes', reviewIds],
    queryFn: () => {
      if (!userProfile) {
        throw new Error('User profile not loaded')
      }
      return getUserLikesForReviews(reviewIds, userProfile.id)
    },
    enabled: reviewIds.length > 0 && !!userProfile,
  })
}

/**
 * Fetch likes counts for multiple reviews
 */
export function useReviewLikesCounts(reviewIds: string[]) {
  return useQuery({
    queryKey: reviewKeys.likesCounts(reviewIds),
    queryFn: () => getReviewLikesCounts(reviewIds),
    enabled: reviewIds.length > 0,
  })
}

/**
 * Check if user has reviewed a fragrance
 */
export function useUserReviewForFragrance(fragranceId: string) {
  const userProfile = useAuthStore((state) => state.userProfile)
  
  return useQuery({
    queryKey: reviewKeys.userReview(fragranceId),
    queryFn: () => {
      if (!userProfile) {
        throw new Error('User profile not loaded')
      }
      return getUserReviewForFragrance(fragranceId, userProfile.id)
    },
    enabled: !!fragranceId && !!userProfile,
  })
}

/**
 * Fetch current user's reviews
 */
export function useCurrentUserReviews(page = 1, perPage = 50) {
  const userProfile = useAuthStore((state) => state.userProfile)
  
  return useQuery({
    queryKey: reviewKeys.currentUserReviews(),
    queryFn: () => {
      if (!userProfile) {
        throw new Error('User profile not loaded')
      }
      return getCurrentUserReviews(page, perPage, userProfile.id)
    },
    enabled: !!userProfile,
  })
}

// ============================================================================
// Reviews Mutations
// ============================================================================

/**
 * Create a new review
 */
export function useCreateReview(fragranceId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ rating, text }: { rating: number; text: string }) =>
      createReview(fragranceId, rating, text),
    onSuccess: () => {
      // Invalidate reviews list for this fragrance
      queryClient.invalidateQueries({ queryKey: reviewKeys.list(fragranceId) })
      // Invalidate average rating
      queryClient.invalidateQueries({ queryKey: reviewKeys.averageRating(fragranceId) })
      // Invalidate user's review check
      queryClient.invalidateQueries({ queryKey: reviewKeys.userReview(fragranceId) })
      // Invalidate current user's reviews list
      queryClient.invalidateQueries({ queryKey: reviewKeys.currentUserReviews() })
    },
  })
}

/**
 * Update an existing review
 */
export function useUpdateReview(fragranceId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ reviewId, rating, text }: { reviewId: string; rating: number; text: string }) =>
      updateReview(reviewId, rating, text),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewKeys.list(fragranceId) })
      queryClient.invalidateQueries({ queryKey: reviewKeys.averageRating(fragranceId) })
      queryClient.invalidateQueries({ queryKey: reviewKeys.currentUserReviews() })
    },
  })
}

/**
 * Delete a review
 */
export function useDeleteReview(fragranceId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (reviewId: string) => deleteReview(reviewId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewKeys.list(fragranceId) })
      queryClient.invalidateQueries({ queryKey: reviewKeys.averageRating(fragranceId) })
      queryClient.invalidateQueries({ queryKey: reviewKeys.userReview(fragranceId) })
      queryClient.invalidateQueries({ queryKey: reviewKeys.currentUserReviews() })
    },
  })
}

/**
 * Toggle like on a review - uses optimistic updates to avoid refetching
 */
export function useToggleReviewLike() {
  const queryClient = useQueryClient()
  const userProfile = useAuthStore((state) => state.userProfile)

  return useMutation({
    mutationFn: (reviewId: string) => {
      if (!userProfile) {
        throw new Error('User profile not loaded')
      }
      return toggleReviewLike(reviewId, userProfile.id)
    },
    onMutate: async (reviewId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [...reviewKeys.all, 'likesCounts'] })
      await queryClient.cancelQueries({ queryKey: reviewKeys.userLikes() })

      // Snapshot previous values
      const previousUserLikes = queryClient.getQueryData<Set<string>>(reviewKeys.userLikes())
      const isCurrentlyLiked = previousUserLikes?.has(reviewId) ?? false

      // Optimistically update user likes
      if (previousUserLikes) {
        const newUserLikes = new Set(previousUserLikes)
        if (isCurrentlyLiked) {
          newUserLikes.delete(reviewId)
        } else {
          newUserLikes.add(reviewId)
        }
        queryClient.setQueryData(reviewKeys.userLikes(), newUserLikes)
      }

      // Optimistically update likes counts - find and update all cached counts containing this reviewId
      const likesCountsQueries = queryClient.getQueriesData<Map<string, number>>({
        queryKey: [...reviewKeys.all, 'likesCounts'],
      })

      const previousLikesCounts: Array<{ queryKey: readonly unknown[]; data: Map<string, number> }> = []
      for (const [queryKey, data] of likesCountsQueries) {
        if (data?.has(reviewId)) {
          previousLikesCounts.push({ queryKey, data: new Map(data) })
          const newCounts = new Map(data)
          const currentCount = newCounts.get(reviewId) ?? 0
          newCounts.set(reviewId, isCurrentlyLiked ? Math.max(0, currentCount - 1) : currentCount + 1)
          queryClient.setQueryData(queryKey, newCounts)
        }
      }

      return { previousUserLikes, previousLikesCounts, isCurrentlyLiked }
    },
    onError: (_err, _reviewId, context) => {
      // Rollback on error
      if (context?.previousUserLikes) {
        queryClient.setQueryData(reviewKeys.userLikes(), context.previousUserLikes)
      }
      if (context?.previousLikesCounts) {
        for (const { queryKey, data } of context.previousLikesCounts) {
          queryClient.setQueryData(queryKey, data)
        }
      }
    },
  })
}

// ============================================================================
// Replies Queries
// ============================================================================

/**
 * Fetch replies for a review
 */
export function useRepliesByReview(reviewId: string) {
  return useQuery({
    queryKey: reviewKeys.replies(reviewId),
    queryFn: () => getRepliesByReview(reviewId),
    enabled: !!reviewId,
  })
}

// ============================================================================
// Replies Mutations
// ============================================================================

/**
 * Create a reply to a review
 */
export function useCreateReviewReply(reviewId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (text: string) => createReviewReply(reviewId, text),
    onSuccess: () => {
      // Invalidate replies for this review
      queryClient.invalidateQueries({ queryKey: reviewKeys.replies(reviewId) })
    },
  })
}

/**
 * Delete a reply
 */
export function useDeleteReviewReply(reviewId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (replyId: string) => deleteReviewReply(replyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewKeys.replies(reviewId) })
    },
  })
}

// ============================================================================
// Report Mutations
// ============================================================================

/**
 * Report a review
 */
export function useReportReview() {
  const userProfile = useAuthStore((state) => state.userProfile)
  
  return useMutation({
    mutationFn: ({ reviewId, reason }: { reviewId: string; reason: string }) => {
      if (!userProfile) {
        throw new Error('User profile not loaded')
      }
      return reportReview(reviewId, reason, userProfile.id)
    },
  })
}

/**
 * Report a reply
 */
export function useReportReply() {
  const userProfile = useAuthStore((state) => state.userProfile)
  
  return useMutation({
    mutationFn: ({ replyId, reason }: { replyId: string; reason: string }) => {
      if (!userProfile) {
        throw new Error('User profile not loaded')
      }
      return reportReply(replyId, reason, userProfile.id)
    },
  })
}
